import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Film, Code2, Radio, CalendarClock } from "lucide-react";
import { api } from "@/lib/api";

const KIND = { phone: [Phone, "Phone screen"], video: [Film, "Video interview"], coding: [Code2, "Coding round"] };
const timeOf = (iso) => new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
const dur = (iso, now) => {
  const s = Math.max(0, Math.floor((now - new Date(iso)) / 1000));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
  return h ? `${h}h ${m}m` : `${m}m ${String(s % 60).padStart(2, "0")}s`;
};

function LiveRow({ i, now }) {
  const [Icon, label] = KIND[i.kind] || [Radio, i.kind];
  return (
    <li className="flex items-center gap-3 py-2.5" data-testid={`live-now-row-${i.kind}-${i.candidate_id}`}>
      <span className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 grid place-items-center shrink-0"><Icon className="w-4 h-4" /></span>
      <div className="min-w-0 flex-1">
        <Link to={`/app/candidates/${i.candidate_id}`} className="font-medium text-slate-900 hover:text-indigo-600 truncate block">{i.candidate_name}</Link>
        <div className="text-xs text-slate-500 truncate">{label} · {i.job_title}</div>
      </div>
      <div className="text-right shrink-0">
        <div className="text-sm font-mono font-semibold text-rose-600" data-testid={`live-now-duration-${i.candidate_id}`}>{i.started_at ? dur(i.started_at, now) : "—"}</div>
        <div className="text-[11px] text-slate-500">{i.started_at ? `since ${timeOf(i.started_at)}` : ""}</div>
      </div>
    </li>
  );
}

export default function LiveNowCard() {
  const [data, setData] = useState(null);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    let alive = true;
    const load = () => api.get("/live-interviews").then((r) => alive && setData(r.data)).catch(() => {});
    load();
    const poll = setInterval(load, 15000), tick = setInterval(() => setNow(Date.now()), 1000);
    return () => { alive = false; clearInterval(poll); clearInterval(tick); };
  }, []);
  const live = data?.live || [], upcoming = data?.upcoming || [];

  return (
    <div className="tp-card p-6 mb-8" data-testid="live-now-card">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 font-display font-semibold">
          <span className="relative flex w-2.5 h-2.5">
            {live.length > 0 && <span className="absolute inline-flex w-full h-full rounded-full bg-rose-400 opacity-75 animate-ping" />}
            <span className={`relative inline-flex w-2.5 h-2.5 rounded-full ${live.length ? "bg-rose-500" : "bg-slate-300"}`} />
          </span>
          Live Interviews
          <span className="text-xs font-medium text-slate-500" data-testid="live-now-count">{data ? `${live.length} in progress` : ""}</span>
        </div>
        <div className="text-xs text-slate-500">refreshes every 15s</div>
      </div>
      {!data ? <div className="text-sm text-slate-500 py-3">Checking…</div>
        : live.length === 0 ? <div className="text-sm text-slate-500 py-3" data-testid="live-now-empty">No interviews running right now.</div>
        : <ul className="divide-y divide-slate-100">{live.map((i, n) => <LiveRow key={`${i.kind}-${i.candidate_id}-${n}`} i={i} now={now} />)}</ul>}
      {upcoming.length > 0 && (
        <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-600 flex items-center gap-1.5" data-testid="live-now-upcoming">
          <CalendarClock className="w-3.5 h-3.5 text-indigo-500" />
          {upcoming.length} booked phone call{upcoming.length === 1 ? "" : "s"} in the next 30 min: {upcoming.map((u) => `${u.candidate_name} (${timeOf(u.scheduled_at)})`).join(", ")}
        </div>
      )}
    </div>
  );
}
